<template>
  <div class="business-hours-display">
    <!-- Business Hours Display Section (shown when not editing) -->
    <div
      v-if="!isEditing"
      class="business-hours-section"
    >
      <div
        v-if="value.length === 0"
        class="no-hours-message"
        @click="addBusinessHours"
      >
        <p class="text-muted">
          No business hours set - Click to add
        </p>
      </div>
      <div
        v-for="(group, index) in groupedBusinessHours"
        :key="index"
        class="business-hour-item d-flex align-items-center mb-2"
        @click="editBusinessHourGroup(group)"
      >
        <span class="hour-display flex-grow-1">
          {{ group.daysDisplay }}: {{ group.openTime }} - {{ group.closeTime }}
        </span>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="ml-2"
          :disabled="disabled"
          @click.stop="editBusinessHourGroup(group)"
        >
          Edit
        </b-button>
        <b-button
          size="sm"
          variant="outline-danger"
          class="ml-2"
          :disabled="disabled"
          @click.stop="removeBusinessHourGroup(group)"
        >
          Remove
        </b-button>
      </div>

      <!-- Add Business Hours Button Section -->
      <div class="add-hours-button-section">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="disabled"
          @click="addBusinessHours"
        >
          Add Business Hours
        </b-button>
      </div>
    </div>

    <!-- Inline Business Hours Editor Form (shown when editing) -->
    <div
      v-if="isEditing"
      class="business-hours-editor-inline"
    >
      <h5 class="mb-3">
        {{ isEditingExisting ? 'Edit' : 'Add' }} Business Hours
      </h5>

      <b-form>
        <b-form-group label="Days of Week">
          <div class="days-of-week-grid">
            <div
              v-for="day in dayOptions"
              :key="day.value"
              class="day-checkbox-wrapper"
              :class="{
                'weekend-day': isWeekendDay(day.value),
                'disabled-day': isDayDisabled(day.value)
              }"
              @click="!isDayDisabled(day.value) && toggleDay(day.value)"
            >
              <div class="custom-day-checkbox">
                <input
                  :id="`day-${day.value}`"
                  type="checkbox"
                  :value="day.value"
                  :checked="currentBusinessHour.selectedDays.includes(day.value)"
                  :disabled="isDayDisabled(day.value)"
                  class="day-checkbox-input"
                  @change="toggleDay(day.value)"
                >
                <label
                  :for="`day-${day.value}`"
                  class="day-checkbox-label"
                  :class="{ 'disabled-label': isDayDisabled(day.value) }"
                >
                  {{ day.text }}
                </label>
              </div>
            </div>
          </div>
        </b-form-group>

        <!-- Business Hours Time Range -->
        <b-form-group label="Business Hours">
          <b-row>
            <b-col md="6">
              <label
                for="open-time"
                class="form-label"
              >Opening Time</label>
              <b-form-input
                id="open-time"
                v-model="currentBusinessHour.openTime"
                type="time"
                required
              />
            </b-col>
            <b-col md="6">
              <label
                for="close-time"
                class="form-label"
              >Closing Time</label>
              <b-form-input
                id="close-time"
                v-model="currentBusinessHour.closeTime"
                type="time"
                required
              />
            </b-col>
          </b-row>
        </b-form-group>

        <!-- Action Buttons -->
        <div class="form-actions mt-3">
          <b-button
            variant="secondary"
            @click="cancelEditing"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            class="ml-2"
            :disabled="!canSave"
            @click="saveBusinessHour"
          >
            {{ isEditingExisting ? 'Update' : 'Save' }} Business Hours
          </b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'business-hours-display',
  props: {
    value: {
      type: Array,
      required: true,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isEditing: false,
      isEditingExisting: false,
      currentBusinessHour: {
        uuid: null,
        selectedDays: [],
        openTime: '08:30',
        closeTime: '17:30'
      },
      dayOptions: [
        { value: 'MON', text: 'Mon' },
        { value: 'TUE', text: 'Tue' },
        { value: 'WED', text: 'Wed' },
        { value: 'THU', text: 'Thu' },
        { value: 'FRI', text: 'Fri' },
        { value: 'SAT', text: 'Sat' },
        { value: 'SUN', text: 'Sun' }
      ]
    }
  },
  computed: {
    groupedBusinessHours() {
      // Group business hours by same open/close times
      const timeGroups = {}

      this.value.forEach(hour => {
        const timeKey = `${hour.openTime}-${hour.closeTime}`
        if (!timeGroups[timeKey]) {
          timeGroups[timeKey] = {
            openTime: hour.openTime,
            closeTime: hour.closeTime,
            days: [],
            uuid: hour.uuid // Store the first UUID found for this group
          }
        }
        timeGroups[timeKey].days.push(hour.dayOfWeek)
        // If we don't have a UUID yet, use this one
        if (!timeGroups[timeKey].uuid && hour.uuid) {
          timeGroups[timeKey].uuid = hour.uuid
        }
      })

      // Convert to display format with properly ordered and formatted days
      return Object.values(timeGroups).map(group => {
        // Sort days in proper order (MON, TUE, WED, etc.)
        const dayOrder = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
        const sortedDays = group.days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))

        // Format consecutive days (e.g., "Mon-Wed" instead of "Mon, Tue, Wed")
        const daysDisplay = this.formatConsecutiveDays(sortedDays)

        return {
          openTime: group.openTime,
          closeTime: group.closeTime,
          days: sortedDays,
          daysDisplay: daysDisplay,
          uuid: group.uuid
        }
      })
    },
    canSave() {
      return (
        this.currentBusinessHour.selectedDays.length > 0 &&
        this.currentBusinessHour.openTime &&
        this.currentBusinessHour.closeTime
      )
    },
    usedDays() {
      // Get all days that are used by other business hours (excluding current editing UUID)
      return this.value
        .filter(hour => hour.uuid !== this.currentBusinessHour.uuid)
        .map(hour => hour.dayOfWeek)
    }
  },
  methods: {
    addBusinessHours() {
      this.isEditing = true
      this.isEditingExisting = false
      this.currentBusinessHour = {
        uuid: this.generateUUID(),
        selectedDays: [],
        openTime: '08:30',
        closeTime: '17:30'
      }
      this.$emit('editing-changed', true)
    },
    editBusinessHourGroup(group) {
      this.isEditing = true
      this.isEditingExisting = true
      this.currentBusinessHour = {
        uuid: group.uuid,
        selectedDays: [...group.days],
        openTime: group.openTime,
        closeTime: group.closeTime
      }
      this.$emit('editing-changed', true)
    },
    removeBusinessHourGroup(group) {
      // Remove all days that belong to this specific group
      const updatedBusinessHours = this.value.filter(hour =>
        !group.days.includes(hour.dayOfWeek) ||
        hour.openTime !== group.openTime ||
        hour.closeTime !== group.closeTime ||
        hour.uuid !== group.uuid
      )
      this.$emit('input', updatedBusinessHours)
    },
    saveBusinessHour() {
      if (!this.canSave) {
        return
      }

      let updatedBusinessHours = [...this.value]

      if (this.currentBusinessHour.uuid) {
        // Remove any existing hours with this UUID
        updatedBusinessHours = updatedBusinessHours.filter(hour => hour.uuid !== this.currentBusinessHour.uuid)
      }

      // Add new business hours for each selected day
      this.currentBusinessHour.selectedDays.forEach(day => {
        const newHour = {
          uuid: this.currentBusinessHour.uuid,
          dayOfWeek: day,
          openTime: this.currentBusinessHour.openTime,
          closeTime: this.currentBusinessHour.closeTime
        }
        updatedBusinessHours.push(newHour)
      })

      this.$emit('input', updatedBusinessHours)
      this.cancelEditing()
    },
    cancelEditing() {
      this.isEditing = false
      this.isEditingExisting = false
      this.resetForm()
      this.$emit('editing-changed', false)
    },
    resetForm() {
      this.currentBusinessHour = {
        uuid: null,
        selectedDays: [],
        openTime: '08:30',
        closeTime: '17:30'
      }
    },
    toggleDay(day) {
      if (this.isDayDisabled(day)) {
        return
      }

      const index = this.currentBusinessHour.selectedDays.indexOf(day)
      if (index === -1) {
        this.currentBusinessHour.selectedDays.push(day)
      }
      else {
        this.currentBusinessHour.selectedDays.splice(index, 1)
      }
    },
    isDayDisabled(day) {
      // When editing, allow all days that belong to the current business hour entry (same UUID)
      if (this.isEditingExisting && this.currentBusinessHour.uuid) {
        // Find if this day belongs to the current business hour being edited
        const currentBusinessHourDays = this.value
          .filter(hour => hour.uuid === this.currentBusinessHour.uuid)
          .map(hour => hour.dayOfWeek)

        // If this day belongs to the current business hour, it should not be disabled
        if (currentBusinessHourDays.includes(day)) {
          return false
        }
      }

      // Disable days that are used by other business hours entries (different UUIDs)
      return this.usedDays.includes(day)
    },
    isWeekendDay(day) {
      return day === 'SAT' || day === 'SUN'
    },
    generateUUID() {
      // Simple UUID v4 generator
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    formatConsecutiveDays(days) {
      if (days.length === 0) return ''
      if (days.length === 1) {
        return this.getDayDisplayName(days[0])
      }

      // Format an array of days into a string, merging consecutive days
      const dayOrder = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
      const orderedDays = days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))

      const result = []
      let rangeStart = 0
      let rangeEnd = 0

      for (let i = 1; i < orderedDays.length; i++) {
        const currentDay = orderedDays[i]
        const prevDay = orderedDays[i - 1]

        // Check if current day is the next in the week sequence
        if (dayOrder.indexOf(currentDay) === dayOrder.indexOf(prevDay) + 1) {
          rangeEnd = i
        }
        else {
          // Not consecutive, push the previous range and start a new one
          if (rangeStart === rangeEnd) {
            result.push(this.getDayDisplayName(orderedDays[rangeStart])) // Single day
          }
          else if (rangeEnd === rangeStart + 1) {
            // Two consecutive days - show both names
            result.push(`${this.getDayDisplayName(orderedDays[rangeStart])}, ${this.getDayDisplayName(orderedDays[rangeEnd])}`)
          }
          else {
            // Three or more consecutive days - show range
            result.push(`${this.getDayDisplayName(orderedDays[rangeStart])}-${this.getDayDisplayName(orderedDays[rangeEnd])}`)
          }
          rangeStart = i
          rangeEnd = i
        }
      }

      // Push the final range
      if (rangeStart === rangeEnd) {
        result.push(this.getDayDisplayName(orderedDays[rangeStart]))
      }
      else if (rangeEnd === rangeStart + 1) {
        result.push(`${this.getDayDisplayName(orderedDays[rangeStart])}, ${this.getDayDisplayName(orderedDays[rangeEnd])}`)
      }
      else {
        result.push(`${this.getDayDisplayName(orderedDays[rangeStart])}-${this.getDayDisplayName(orderedDays[rangeEnd])}`)
      }

      return result.join(', ')
    },
    getDayDisplayName(dayCode) {
      const dayMap = {
        MON: 'Mon',
        TUE: 'Tue',
        WED: 'Wed',
        THU: 'Thu',
        FRI: 'Fri',
        SAT: 'Sat',
        SUN: 'Sun'
      }
      return dayMap[dayCode] || dayCode
    }
  }
}
</script>

<style lang="scss" scoped>
.business-hours-section {
    .business-hour-item {
      background-color: #f8f9fa;
      padding: 0.75rem;
      border-radius: 0.375rem;
      border: 1px solid #dee2e6;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
        box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
      }

      .hour-display {
        font-weight: 500;
        color: #495057;
      }
    }

    .no-hours-message {
      padding: 1rem;
      text-align: center;
      border: 2px dashed #dee2e6;
      border-radius: 0.375rem;
      background-color: #f8f9fa;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
        background-color: #e7f3ff;
      }
    }

    .add-hours-button-section {
      margin-top: 1.5rem;
      padding-top: 1rem;
      display: flex;
      justify-content: flex-end;
      border-top: 1px solid #e9ecef;
    }
  }

.business-hours-editor-inline {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;

  .days-of-week-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.5rem;
    margin-bottom: 1rem;

    .day-checkbox-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &.weekend-day {
        .day-checkbox-label {
          color: #6f42c1;
          font-weight: 500;
        }
      }

      &.disabled-day {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .custom-day-checkbox {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        .day-checkbox-input {
          margin-bottom: 0.25rem;
        }

        .day-checkbox-label {
          font-size: 0.875rem;
          margin: 0;
          cursor: pointer;

          &.disabled-label {
            cursor: not-allowed;
            color: #6c757d;
          }
        }
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
  }
}
</style>
